import { useMemo } from 'react'

import { useParams } from 'react-router'
import { LoadingDots } from 'src/components/LoadingDots/LoadingDots'
import { useAuthenticatedAccount } from 'src/features/auth/context'
import { Member } from 'src/features/member/types'
import { VideoCallRoom } from 'src/features/videoCall/components'

import {
  GetPatientVideoCallReturn,
  usePatientVideoCall,
} from '../../api/getPatientVideoCall'
import { usePatientVideoCallToken } from '../../hooks/usePatientVideoCallToken'

type Param = {
  patientVideoCallUid: string
  trialUid: string
}

const getEndedText = (isPartner: boolean) => {
  if (isPartner) {
    return 'ビデオ通話を終了しました。\n端末を医療スタッフへお返しください。'
  }
  return 'ビデオ通話を終了しました。'
}

export const PatientVideoCallScreen: React.FC = () => {
  const { patientVideoCallUid = '' } = useParams<Param>()

  const data = usePatientVideoCallToken({
    patientVideoCallUid,
  })

  const { data: patientVideoCall } = usePatientVideoCall({
    patientVideoCallUid,
  })
  if (!data) {
    return <LoadingDots />
  }

  const { token, videoCallUserUid, screenShareToken, screenShareUserUid } = data

  return (
    patientVideoCall &&
    !!data.token && (
      <AuthChecker patientVideoCall={patientVideoCall}>
        {({ isPartner }) => (
          <VideoCallRoom
            token={token}
            endedText={getEndedText(isPartner)}
            videoCallUserUid={videoCallUserUid}
            screenShareUserUid={screenShareUserUid}
            screenShareToken={screenShareToken}
            canShareScreen={true}
            roomUid={patientVideoCall.videoCallRoomUid}
            agoraCloudProxyMode={data.agoraCloudProxyMode}
            scene="ESource"
          />
        )}
      </AuthChecker>
    )
  )
}

type AuthCheckerProps = {
  patientVideoCall: GetPatientVideoCallReturn
  children: ({
    isPartner,
    member,
  }: {
    isPartner: boolean
    member: Member
  }) => React.ReactNode
}

const AuthChecker: React.FC<AuthCheckerProps> = ({
  patientVideoCall,
  children,
}) => {
  const {
    account: { selectedTrialHospitalUid, member },
  } = useAuthenticatedAccount()

  const childNode = useMemo(() => {
    if (
      patientVideoCall.__typename === 'PatientVideoCallPartnerTrialHospital' &&
      patientVideoCall.trialHospitalUid === selectedTrialHospitalUid
    ) {
      return children({ isPartner: true, member })
    }
    return children({ isPartner: false, member })
  }, [children, patientVideoCall, selectedTrialHospitalUid, member])

  return <>{childNode}</>
}
